import { memo } from "react";
/**
 * @fileoverview ACTIVE VIEW SWITCHING COMPONENT
 * @active Used in current Next.js App Router routes
 * 
 * This component provides the UI controls for switching between TABLE VIEW and CARD VIEW
 * in the companies page. It's the CURRENT ACTIVE implementation for view switching.
 * 
 * Key functionality:
 * - Displays icons for switching between table and card views
 * - Updates the Redux state via setIsCompaniesCards action
 * - Resets filters and parameters when switching views
 * - Fetches appropriate data with adjusted pagination for each view
 * 
 * The view state is controlled by the `isCards` value in Redux:
 * - When false: TABLE VIEW is active (NewCompaniesList)
 * - When true: CARD VIEW is active (NewCompaniesCards)
 * 
 * This component is used by NewCompanies.tsx which conditionally renders
 * either the table or card view based on the isCards state.
 * 
 * @see {@link src/components/companies/NewCompanies.tsx} - ACTIVE component that uses this
 * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Redux state for view mode
 * @see {@link src/assets/svg/block-icon.svg} - Card view icon
 * @see {@link src/assets/svg/list-icon.svg} - List view icon
 * @see {@link @/components/companies/companiesList/newCompaniesList/index.tsx} - TABLE VIEW implementation
 * @see {@link @/components/companies/newCompaniesCards/index.jsx} - CARD VIEW implementation
 * @see {@link @/store/newCompaniesList/newCompaniesList.slice.ts} - Redux slice with isCards state
 */

import { Flex, Icon } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import BlockIconSvg from "@/assets/svg/block-icon.svg";
import ListIconSvg from "@/assets/svg/list-icon.svg";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";

import { useActions } from "../../../hooks/useActions";




const SwitchViewBtns = () => {
  const isCards = useSelector(newCompaniesListSelectors.getIsCompaniesCards());
  const { setIsCompaniesCards, clearFilterArr, setViewParams, setListParams } =
    useActions();

  const { getCompanies } = useCompaniesListRequest();

  const clearParams = () => {
    clearFilterArr({ key: "default" });
    setViewParams({
      key: "default",
      params: {
        arrayFilters: undefined,
        orderBy: undefined,
        direction: undefined,
        limit: 25,
        text: undefined,
      },
    });
    setListParams({ page: 1 });
  };

  const onSwitchToCardHandler = () => {
    clearParams();
    getCompanies({ params: { limit: 24, page: 1 } });
    setIsCompaniesCards(true);
  };
  const onSwitchToListHandler = () => {
    clearParams();
    getCompanies({ params: { limit: 25, page: 1 } });
    setIsCompaniesCards(false);
  };

  return (
    <Flex align={"center"} columnGap={"6px"}>
      <Icon
        as={BlockIconSvg}
        color={!isCards ? "borderGray" : "mainBlue"}
        onClick={() => onSwitchToCardHandler()}
        cursor={"pointer"}
      />
      <Icon
        as={ListIconSvg}
        color={isCards ? "borderGray" : "mainBlue"}
        onClick={() => onSwitchToListHandler()}
        cursor={"pointer"}
      />
    </Flex>
  );
};

export default memo(SwitchViewBtns);
